import type { QueueItem, QueuedFile, QueueStatus } from './types';

/**
 * 队列的本地存储，用 IndexedDB。
 *
 * 为什么不用 @capacitor-community/sqlite：IndexedDB 在安卓 WebView 和
 * 桌面浏览器里行为一致，一套实现两端跑，还能直接存 Blob。多引一个原生插件
 * 只会多一条平台分支和一份要维护的降级逻辑。
 *
 * 数据必须挺过进程被杀：工人拍完照随手切走，系统回收 WebView 是常事。
 */

const DB_NAME = 'construction-queue';
const DB_VERSION = 1;
const ITEMS = 'items';
const FILES = 'files';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(ITEMS)) {
        const store = db.createObjectStore(ITEMS, { keyPath: 'id' });
        // 按 userId 取队列：手机多人共用，只能处理当前登录者的数据
        store.createIndex('userId', 'userId');
        store.createIndex('status', 'status');
      }

      if (!db.objectStoreNames.contains(FILES)) {
        const store = db.createObjectStore(FILES, { keyPath: 'id' });
        store.createIndex('itemId', 'itemId');
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB 打开失败'));
    request.onblocked = () => reject(new Error('IndexedDB 被其他标签页锁住'));
  });

  return dbPromise;
}

function tx<T>(
  stores: string[],
  mode: IDBTransactionMode,
  run: (transaction: IDBTransaction) => Promise<T> | T,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(stores, mode);
        let result: T;
        transaction.oncomplete = () => resolve(result);
        transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB 事务失败'));
        transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB 事务中断'));
        Promise.resolve(run(transaction))
          .then((value) => {
            result = value;
          })
          .catch(reject);
      }),
  );
}

function req<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB 操作失败'));
  });
}

// ---------------------------------------------------------------- 条目

export async function putItem(item: QueueItem): Promise<void> {
  await tx([ITEMS], 'readwrite', (transaction) =>
    req(transaction.objectStore(ITEMS).put(item)),
  );
}

export async function getItem(id: string): Promise<QueueItem | undefined> {
  return tx([ITEMS], 'readonly', (transaction) =>
    req(transaction.objectStore(ITEMS).get(id) as IDBRequest<QueueItem | undefined>),
  );
}

export async function listItems(userId: number): Promise<QueueItem[]> {
  const items = await tx([ITEMS], 'readonly', (transaction) =>
    req(
      transaction.objectStore(ITEMS).index('userId').getAll(userId) as IDBRequest<QueueItem[]>,
    ),
  );
  return items.sort((a, b) => a.createdAt - b.createdAt);
}

/** 取下一个该处理的条目：属于当前用户、到了重试时间、还没成功 */
export async function nextPending(userId: number, now: number): Promise<QueueItem | undefined> {
  const items = await listItems(userId);
  return items.find(
    (item) =>
      (item.status === 'PENDING' || item.status === 'UPLOADING') && item.nextAttemptAt <= now,
  );
}

export async function deleteItem(id: string): Promise<void> {
  await tx([ITEMS, FILES], 'readwrite', async (transaction) => {
    const files = (await req(
      transaction.objectStore(FILES).index('itemId').getAllKeys(id),
    )) as IDBValidKey[];
    for (const key of files) transaction.objectStore(FILES).delete(key);
    transaction.objectStore(ITEMS).delete(id);
  });
}

/**
 * 把上次进程被杀时卡在 UPLOADING 的条目复位。
 * 不复位的话它们永远轮不到，工人的照片就静静地留在手机里传不上去。
 */
export async function resetStuck(): Promise<number> {
  return tx([ITEMS], 'readwrite', async (transaction) => {
    const store = transaction.objectStore(ITEMS);
    const all = (await req(store.getAll())) as QueueItem[];
    const stuck = all.filter((item) => item.status === 'UPLOADING');
    for (const item of stuck) {
      store.put({ ...item, status: 'PENDING' as QueueStatus, updatedAt: Date.now() });
    }
    return stuck.length;
  });
}

// ---------------------------------------------------------------- 文件

export async function putFile(file: QueuedFile): Promise<void> {
  await tx([FILES], 'readwrite', (transaction) => req(transaction.objectStore(FILES).put(file)));
}

export async function listFiles(itemId: string): Promise<QueuedFile[]> {
  const files = await tx([FILES], 'readonly', (transaction) =>
    req(
      transaction.objectStore(FILES).index('itemId').getAll(itemId) as IDBRequest<QueuedFile[]>,
    ),
  );
  return files.sort((a, b) => a.id.localeCompare(b.id));
}

export async function countPendingFiles(userId: number): Promise<number> {
  const items = await listItems(userId);
  const active = items.filter((item) => item.status !== 'DONE');
  const counts = await Promise.all(active.map((item) => listFiles(item.id).then((f) => f.length)));
  return counts.reduce((sum, n) => sum + n, 0);
}

/** 清掉已完成的条目和它们的文件，别让 IndexedDB 无限长大 */
export async function purgeDone(userId: number): Promise<number> {
  const items = await listItems(userId);
  const done = items.filter((item) => item.status === 'DONE');
  for (const item of done) await deleteItem(item.id);
  return done.length;
}
